import React, {Component} from 'react';
import {RadioButtonOption} from "../../elements/RadioButtonOption";
import {
    DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_COST_AND_BUDGET, DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_ONLY_COST,
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET, DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_COST,
    DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_COST_AND_BUDGET, DAILY_LIMIT_TYPES_AT_CAMPAIGN_LEVEL,
    CAMPAIGN_OPTION_I18N_KEY
} from "../../../daily_limits";
import {CHANNEL_INFO_BY_CHANNEL} from "../../channels/channels";
import BudgetValueInput from "../../elements/BudgetValueInput";
import { withTranslation } from 'react-i18next';


class ClientAdAccountFormBaseClass extends Component {
    constructor(props) {
        super(props);

        this.state = {
            dailyLimitEnabled: true,
            daily_limit_type: DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_COST_AND_BUDGET,
            daily_limit_value: null,
            daily_limit_currency: "ARS"
        }

        this.handleDailyLimitChange = this.handleDailyLimitChange.bind(this);
        this.handleDailyLimitDeactivate = this.handleDailyLimitDeactivate.bind(this);
        this.handleCurrencyChange = this.handleCurrencyChange.bind(this);
        this.handleAmountChange = this.handleAmountChange.bind(this);
    }

    _getDataToPost() {
        const daily_limit_currency = this.state.dailyLimitEnabled ? this.state.daily_limit_currency : null;
        const daily_limit_value = this.state.dailyLimitEnabled ? this.state.daily_limit_value : null;

        return {
            'daily_limit_type': this.state.daily_limit_type,
            'daily_limit_currency': daily_limit_currency,
            'daily_limit_value': daily_limit_value,
        };
    }

    handleDailyLimitChange(newType) {
        this.setState({
            daily_limit_type: newType,
            dailyLimitEnabled: true
        });
    }

    handleCurrencyChange(newCurrency) {
        this.setState({
            daily_limit_currency: newCurrency
        });
    }

    handleAmountChange(newAmount) {
        this.setState({
            daily_limit_value: newAmount
        });
    }

    handleDailyLimitDeactivate() {
        this.setState({
            dailyLimitEnabled: false,
        });
    }

    isAdAccountTypeSelected() {
        return this.state.dailyLimitEnabled && this.getDailyLimitType() === DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_COST_AND_BUDGET
    }

    isCampaignTypeSelected(type) {
        return this.state.dailyLimitEnabled && this.getDailyLimitType() === type
    }

    // El canal donde se está configurando el límite. Lo resuelven las subclases: el form de alta lo
    // tiene en su state (se elige en la pantalla) y el de edición en la cuenta que recibe por props.
    currentChannel() {
        return null;
    }

    channelCanReportCost(channel) {
        const channelInfo = channel ? CHANNEL_INFO_BY_CHANNEL[channel] : null;
        return channelInfo ? channelInfo.dailyLimitOnCostSupported === true : false;
    }

    channelHasDailyLimit(channel) {
        const channelInfo = channel ? CHANNEL_INFO_BY_CHANNEL[channel] : null;
        return channelInfo ? channelInfo.dailyLimitEnabled === true : false;
    }

    // El tipo que la cuenta tiene GUARDADO. Lo resuelve el form de edición; en el alta no hay nada
    // guardado todavía. NO es `state.daily_limit_type`: ese es la selección viva, que cambia en cada
    // clic — usarlo para decidir qué opciones mostrar hacía desaparecer la opción vieja apenas el
    // usuario tocaba otra, que es el único momento en que el modal existe.
    savedDailyLimitType() {
        return null;
    }

    campaignTypeForChannel(channel) {
        // A nivel campaña se ofrece UNA sola opción, como en el nivel cuenta: la combinada donde el
        // canal sabe reportar consumo, y la de presupuesto donde no. Ofrecer las tres obligaba a
        // elegir entre controles que casi siempre se quieren juntos, y en los canales sin costo dos
        // de ellas eran promesas que nunca podían sonar.
        return this.channelCanReportCost(channel)
            ? DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_COST_AND_BUDGET
            : DAILY_LIMIT_TYPE_DAILY_CAMPAIGN_ONLY_BUDGET;
    }

    campaignTypesToOffer() {
        const forChannel = this.campaignTypeForChannel(this.currentChannel());
        const saved = this.savedDailyLimitType();
        // Un tipo que la cuenta YA tiene guardado se sigue ofreciendo aunque no sea el que le toca a
        // su canal: esconderlo la dejaría con un límite activo que la pantalla no muestra. Va
        // PRIMERO porque es el que la cuenta tiene hoy, y porque los dos rótulos se parecen bastante
        // como para que el orden importe.
        if (DAILY_LIMIT_TYPES_AT_CAMPAIGN_LEVEL.includes(saved) && saved !== forChannel)
            return [saved, forChannel];
        return [forChannel];
    }

    alignDailyLimitTypeWithChannel(newChannel) {
        // Un canal SIN límite diario (GA4, tiendas, GSC) esconde la sección entera. Si además queda
        // `dailyLimitEnabled` en true, `_getDataToPost` manda igual tipo, monto y moneda: se creaba
        // una cuenta con un límite diario que el usuario nunca vio. Apagarlo deja el payload limpio
        // por el nulleo que ya hace `_getDataToPost`.
        if (!this.channelHasDailyLimit(newChannel)) {
            if (this.state.dailyLimitEnabled)
                this.setState({dailyLimitEnabled: false});
            return;
        }

        // Al cambiar de canal en el alta hay que mover la selección EN LAS DOS DIRECCIONES: el tipo
        // que el canal nuevo no ofrece quedaría elegido en el state con su opción fuera de la
        // pantalla, y se enviaría igual. Conserva el nivel campaña y sólo cambia qué mira; el cambio
        // se ve (la selección se mueve), que es la diferencia con degradar en silencio.
        if (!DAILY_LIMIT_TYPES_AT_CAMPAIGN_LEVEL.includes(this.state.daily_limit_type))
            return;
        const target = this.campaignTypeForChannel(newChannel);
        if (this.state.daily_limit_type !== target)
            this.setState({daily_limit_type: target});
    }

    getDailyLimitType() {
        return this.state.daily_limit_type ? this.state.daily_limit_type : DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_ONLY_COST
    }

    getDailyLimitCurrency() {
        // Sino tiene ningún valor, poner ARS como default
        return this.state.daily_limit_currency ? this.state.daily_limit_currency : "ARS"
    }

    renderRadioButtonOptions() {
        const { t } = this.props;
        return [
            <RadioButtonOption
                key="adAccount"
                isSelected={this.isAdAccountTypeSelected()}
                onClick={() => this.handleDailyLimitChange(DAILY_LIMIT_TYPE_DAILY_AD_ACCOUNT_COST_AND_BUDGET)}
                label={<span>{t('client_ad_account_form_base.daily_limit_ad_account')}</span>}
                note={t('client_ad_account_form_base.daily_limit_ad_account_note')}>
                {this.renderDailyLimitInputs()}
            </RadioButtonOption>,
            ...this.campaignTypesToOffer().map((type) => (
                <RadioButtonOption
                    key={type}
                    isSelected={this.isCampaignTypeSelected(type)}
                    onClick={() => this.handleDailyLimitChange(type)}
                    label={<span>{t('client_ad_account_form_base.' + CAMPAIGN_OPTION_I18N_KEY[type])}</span>}
                    note={t('client_ad_account_form_base.' + CAMPAIGN_OPTION_I18N_KEY[type] + '_note')}>
                    {this.renderDailyLimitInputs()}
                </RadioButtonOption>
            )),
            <RadioButtonOption
                key="deactivate"
                isSelected={!this.state.dailyLimitEnabled}
                onClick={this.handleDailyLimitDeactivate}
                label={t('client_ad_account_form_base.no_daily_limit')}
            />
        ];
    }

    renderDailyLimitInputs() {
        if (!this.state.dailyLimitEnabled)
            return;

        return <div style={{maxWidth: "250px"}}><BudgetValueInput canEditCurrency={true}
                                                                  hideCurrency={false}
                                                                  budgetCurrency={this.getDailyLimitCurrency()}
                                                                  currencyReadOnly={false}
                                                                  onCurrencyChange={this.handleCurrencyChange}
                                                                  onAmountChange={this.handleAmountChange}
                                                                  budgetAmount={this.state.daily_limit_value}/></div>
    }
}

export { ClientAdAccountFormBaseClass };
export const ClientAdAccountFormBase = withTranslation()(ClientAdAccountFormBaseClass);
export default ClientAdAccountFormBase;